'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { useIntl } from 'react-intl'

import {
  AnalyticsSource,
  AnalyticsEntityType,
} from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import { useIsMobileTableLayout } from '@/components/listings/shared/useIsMobileTableLayout'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useListingSearch } from '../ListingSearchProvider'
import { getDefaultSort } from '../ListingSearchProvider/defaultState'
import {
  SearchForm,
  ClearIcon,
  InputSearch,
  SubmitButton,
  SearchIcon,
  ClearButton,
} from './ui'

function StorefrontListingSearch({
  storefront,
  onChange,
  applyOnSubmit = true,
}: {
  storefront: StoreFrontDto
  onChange?: (value: string) => void
  applyOnSubmit?: boolean
}) {
  const {
    state: { filters, view },
    updateFilters,
    setSort,
  } = useListingSearch()

  const { trackButtonClicked } = useAnalytics()
  const isMobileFiltersShown = useIsMobileTableLayout()
  const device = isMobileFiltersShown ? 'Mobile' : 'Desktop'
  const [query, _setQuery] = useState(filters.q ?? '')
  const setQuery = useCallback(
    (value: string) => {
      _setQuery(value)
      onChange?.(value)
    },
    [onChange]
  )

  useEffect(() => {
    _setQuery(filters.q || '') // oxlint-disable-line react/set-state-in-effect
  }, [filters.q])

  const applySearch = useCallback(
    (value: string, trackAnalytics = true) => {
      if (value.trim() && trackAnalytics) {
        trackButtonClicked({
          buttonName: 'filters',
          filter_title: 'search_storefront',
          search_term: value.trim(),
          is_selected: true,
          device,
          alphabetical_filter: false,
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
        })
      }

      if (applyOnSubmit) {
        updateFilters({ q: value || null })
        setSort(
          ...getDefaultSort({
            hasQuery: !!value,
            view,
          })
        )
      }
    },
    [applyOnSubmit, device, setSort, trackButtonClicked, updateFilters, view]
  )

  const inputRef = useRef<HTMLInputElement | null>(null)

  const { formatMessage } = useIntl()
  const label = formatMessage(
    {
      id: 'Search.storefront.placeholder',
    },
    {
      storefront: storefront.name,
    }
  )
  return (
    <SearchForm
      onSubmit={(e) => {
        e.preventDefault()
        applySearch(query)
      }}
      role="search"
      aria-label={label}
    >
      <InputSearch
        type="text"
        placeholder={label}
        name="storefront-searchQuery"
        onChange={(e) => {
          setQuery(e.target.value)
        }}
        value={query}
        data-testid="search-input-field"
        aria-label={label}
        autoComplete="off"
        ref={inputRef}
      />
      {query.length > 0 && (
        <ClearButton
          type="button"
          aria-label={formatMessage({
            id: 'Search.storefront.clear',
          })}
          data-testid="search-input-clear-btn"
          onClick={() => {
            // Track clear search analytics
            trackButtonClicked({
              buttonName: 'filters',
              filter_title: 'search_storefront',
              search_term: '', // Empty search term
              is_selected: false,
              device,
              alphabetical_filter: false,
              source: AnalyticsSource.HOME_PORTAL,
              entityType: AnalyticsEntityType.BUYER,
            })

            setQuery('')
            applySearch('')
          }}
        >
          <ClearIcon icon={faTimes} />
        </ClearButton>
      )}

      <SubmitButton
        type="submit"
        data-testid="search-submit-btn"
        aria-label={formatMessage({
          id: 'Search.storefront.submit',
        })}
      >
        <SearchIcon icon={faSearch} />
      </SubmitButton>
    </SearchForm>
  )
}

export default StorefrontListingSearch
